<template>
    <landlords-find
        :params="{
            landlord: $route.params.landlord
        }"
        :include="[
            'account(include:business(include:address,proof_of_entity),persons(include:address,identity_card,proof_of_address))'
        ]"
        @success="onLandlordsFind"
    >
        <div
            slot-scope="{
                data: landlord,
                error: find_error,
                has_loaded,
                find
            }"
        >
            <template v-if="!has_loaded || find_error">
                <base-loading
                    :error="find_error"
                    :retry="find"
                    class="py-6"
                />

                <error-modal
                    v-if="find_error"
                    :error="find_error"
                />
            </template>

            <businesses-store v-else>
                <persons-updates
                    slot-scope="{
                        sid: business_sid,
                        promise: businessPromise
                    }"
                    :include="[
                        'address',
                        'identity_card',
                        'proof_of_address',
                    ]"
                >
                    <promise-all
                        slot-scope="{
                            sid: persons_sid,
                            promise: personsPromise
                        }"
                        :execute-on-created="false"
                        :callstack="{
                            business: () => businessPromise(forms.business),
                            persons: () => personsPromise(forms.persons)
                        }"
                        @success="onSuccess"
                        @error="onError"
                    >
                        <base-form
                            slot-scope="{
                                is_loading: is_saving,
                                errors,
                                execute
                            }"
                            @submit="execute"
                        >
                            <div class="px-4 pt-6 border-b sm:px-6">
                                <validator-alert :sid="[business_sid, persons_sid]" />

                                <form-section>
                                    <template slot="title">Legal Details</template>
                                    <template slot="description">
                                        These values should the values registered with
                                        <a
                                            href="https://beta.companieshouse.gov.uk/"
                                            class="text-blue"
                                        >
                                            Companies House.
                                        </a>
                                    </template>

                                    <grid-layout>
                                        <grid-layout-item class="md:w-1/2">
                                            <form-item
                                                :sid="business_sid"
                                                :is-required="true"
                                                name="business[legal_name]"
                                                validator-name="legal_name"
                                            >
                                                <template slot="label">Legal Name</template>

                                                <input-text
                                                    v-model="forms.business.legal_name"
                                                    :sid="business_sid"
                                                    :disabled="is_saving || landlord.account.number_of_verification_attempts > 0"
                                                    placeholder="Rentora Ltd."
                                                    name="business[legal_name]"
                                                    validator-name="legal_name"
                                                />
                                            </form-item>
                                        </grid-layout-item>

                                        <grid-layout-item class="md:w-1/2">
                                            <form-item
                                                :sid="business_sid"
                                                :is-required="true"
                                                name="business[tax_id]"
                                                validator-name="tax_id"
                                            >
                                                <template slot="label">CRN</template>

                                                <template slot="help">Company Registration Number</template>

                                                <input-text
                                                    v-model="forms.business.tax_id"
                                                    :sid="business_sid"
                                                    :disabled="is_saving || landlord.account.number_of_verification_attempts > 0"
                                                    placeholder="XX000000"
                                                    name="business[tax_id]"
                                                    validator-name="tax_id"
                                                />
                                            </form-item>
                                        </grid-layout-item>

                                        <grid-layout-item class="md:w-1/2">
                                            <form-item
                                                :sid="business_sid"
                                                name="business[vat_id]"
                                                validator-name="vat_id"
                                            >
                                                <template slot="label">VAT Number</template>

                                                <input-text
                                                    v-model="forms.business.vat_id"
                                                    :sid="business_sid"
                                                    :disabled="is_saving || landlord.account.number_of_verification_attempts > 0"
                                                    placeholder="GB00000000"
                                                    name="business[vat_id]"
                                                    validator-name="vat_id"
                                                />
                                            </form-item>
                                        </grid-layout-item>
                                    </grid-layout>
                                </form-section>
                            </div>

                            <div class="px-4 pt-6 border-b sm:px-6">
                                <form-section>
                                    <template slot="title">Customer-facing Details</template>
                                    <template slot="description">
                                        Often, your trading name is different to your legally registered name.
                                    </template>

                                    <grid-layout>
                                        <grid-layout-item class="md:w-1/2">
                                            <form-item
                                                :sid="business_sid"
                                                :is-required="true"
                                                name="business[name]"
                                                validator-name="name"
                                            >
                                                <template slot="label">Name</template>

                                                <input-text
                                                    v-model="forms.business.name"
                                                    :sid="business_sid"
                                                    :disabled="is_saving"
                                                    placeholder="Rentora"
                                                    name="business[name]"
                                                    validator-name="name"
                                                />
                                            </form-item>
                                        </grid-layout-item>

                                        <grid-layout-item class="md:w-1/2">
                                            <form-item
                                                :sid="business_sid"
                                                :is-required="true"
                                                name="business[telephone]"
                                                validator-name="telephone"
                                            >
                                                <template slot="label">Telephone</template>

                                                <input-telephone
                                                    v-model="forms.business.telephone"
                                                    :sid="business_sid"
                                                    :disabled="is_saving"
                                                    name="business[telephone]"
                                                    validator-name="telephone"
                                                />
                                            </form-item>
                                        </grid-layout-item>
                                    </grid-layout>
                                </form-section>
                            </div>

                            <div class="px-4 pt-6 border-b sm:px-6">
                                <form-section>
                                    <template slot="title">Address</template>
                                    <template slot="description">
                                        We now need the address of <strong>the business.</strong>
                                    </template>

                                    <form-items-address
                                        v-model="forms.business.address"
                                        :sid="business_sid"
                                        :disabled="is_saving"
                                        name="business[address]"
                                        validator-name="address"
                                    />
                                </form-section>
                            </div>

                            <div class="px-4 pt-6 border-b sm:px-6">
                                <form-section>
                                    <template slot="title">Proof of Entity</template>
                                    <template slot="description">
                                        Upload a clear, high-resolution scan (or photo) of your
                                        <a
                                            href="https://help.rentora.co.uk/article/49-what-proof-of-entity-documents-are-accetable"
                                            class="text-blue"
                                            target="_blank"
                                            rel="noopener"
                                        >
                                            proof of entity.
                                        </a>
                                    </template>

                                    <grid-layout>
                                        <grid-layout-item>
                                            <form-item-identity-document
                                                v-model="forms.business.proof_of_entity"
                                                :identity-document="landlord.account.business ? landlord.account.business.proof_of_entity : null"
                                                :sid="business_sid"
                                                name="persons[proof_of_entity]"
                                                validator-name="proof_of_entity"
                                                :disabled="is_saving"
                                                :is-required="true"
                                                :is-double-sided="false"
                                            />
                                        </grid-layout-item>
                                    </grid-layout>
                                </form-section>
                            </div>

                            <div class="px-4 py-6 border-b sm:px-6">
                                <form-section>
                                    <template slot="title">People</template>
                                    <template slot="description">
                                        <div class="mb-3">
                                            In addition to you, we also need details of any people who are <strong>directors, executives and/or owners</strong>.
                                        </div>
                                    </template>

                                    <base-alert
                                        v-if="$validator.has('meta.percent_ownership', persons_sid)"
                                        level="danger"
                                    >
                                        <template slot="heading">Percent Ownership</template>
                                        <template slot="content">
                                            {{ $validator.first('meta.percent_ownership', persons_sid) }}
                                        </template>
                                    </base-alert>

                                    <div
                                        v-for="(form, index) in forms.persons"
                                        :key="index"
                                        class="relative mt-3 mb-6 border rounded"
                                    >
                                        <with-variables
                                            :variables="{
                                                person: findPerson(landlord.account.persons, form.uuid)
                                            }"
                                            v-slot="{
                                                person
                                            }"
                                        >
                                            <div>
                                                <header class="flex items-center px-4 py-3 text-xs border-b text-label bg-grey-lightest">
                                                    <div class="flex-grow">
                                                        {{ index + 1 | ordinal }} Person

                                                        <template v-if="form.is_account_opener">
                                                            (You)
                                                        </template>
                                                    </div>

                                                    <button
                                                        v-if="!form.is_account_opener"
                                                        type="button"
                                                        class="flex-none w-5 h-5 focus:outline-none"
                                                        @click.prevent="removePerson(form, index)"
                                                    >
                                                        <base-svg
                                                            name="icon-trash"
                                                            class="w-full h-full"
                                                        />
                                                    </button>
                                                </header>

                                                <template v-if="!form.is_account_opener">
                                                    <div class="px-4 pt-3 border-b">
                                                        <h6 class="mb-3 text-base font-normal">Basics</h6>

                                                        <grid-layout>
                                                            <grid-layout-item class="md:w-1/2">
                                                                <form-item
                                                                    :sid="persons_sid"
                                                                    :is-required="true"
                                                                    :name="`persons[${index}][first_name]`"
                                                                    :validator-name="`data.${index}.first_name`"
                                                                >
                                                                    <template slot="label">First Name</template>

                                                                    <input-text
                                                                        v-model="forms.persons[index].first_name"
                                                                        :sid="persons_sid"
                                                                        :name="`persons[${index}][first_name]`"
                                                                        :validator-name="`data.${index}.first_name`"
                                                                        :disabled="is_saving || (person && person.status === person.consts.status.verified)"
                                                                    />
                                                                </form-item>
                                                            </grid-layout-item>

                                                            <grid-layout-item class="md:w-1/2">
                                                                <form-item
                                                                    :sid="persons_sid"
                                                                    :is-required="true"
                                                                    :name="`persons[${index}][last_name]`"
                                                                    :validator-name="`data.${index}.last_name`"
                                                                >
                                                                    <template slot="label">Last Name</template>

                                                                    <input-text
                                                                        v-model="forms.persons[index].last_name"
                                                                        :sid="persons_sid"
                                                                        :name="`persons[${index}][last_name]`"
                                                                        :validator-name="`data.${index}.last_name`"
                                                                        :disabled="is_saving || (person && person.status === person.consts.status.verified)"
                                                                    />
                                                                </form-item>
                                                            </grid-layout-item>

                                                            <grid-layout-item class="md:w-1/2">
                                                                <form-item
                                                                    :sid="persons_sid"
                                                                    :is-required="true"
                                                                    :name="`persons[${index}][date_of_birth]`"
                                                                    :validator-name="`data.${index}.date_of_birth`"
                                                                >
                                                                    <template slot="label">Date of Birth</template>

                                                                    <input-date
                                                                        v-model="forms.persons[index].date_of_birth"
                                                                        :sid="persons_sid"
                                                                        :name="`persons[${index}][date_of_birth]`"
                                                                        :validator-name="`data.${index}.date_of_birth`"
                                                                        :disabled="is_saving || (person && person.status === person.consts.status.verified)"
                                                                    />
                                                                </form-item>
                                                            </grid-layout-item>
                                                        </grid-layout>
                                                    </div>

                                                    <div class="px-4 pt-3 border-b">
                                                        <h6 class="mb-3 text-base font-normal">Contact</h6>

                                                        <grid-layout>
                                                            <grid-layout-item class="md:w-1/2">
                                                                <form-item
                                                                    :sid="persons_sid"
                                                                    :is-required="true"
                                                                    :name="`persons[${index}][email]`"
                                                                    :validator-name="`data.${index}.email`"
                                                                >
                                                                    <template slot="label">Email</template>

                                                                    <input-text
                                                                        v-model="forms.persons[index].email"
                                                                        :sid="persons_sid"
                                                                        :name="`persons[${index}][email]`"
                                                                        :validator-name="`data.${index}.email`"
                                                                        :disabled="is_saving"
                                                                    />
                                                                </form-item>
                                                            </grid-layout-item>

                                                            <grid-layout-item class="md:w-1/2">
                                                                <form-item
                                                                    :sid="persons_sid"
                                                                    :is-required="true"
                                                                    :name="`persons[${index}][telephone]`"
                                                                    :validator-name="`data.${index}.telephone`"
                                                                >
                                                                    <template slot="label">Telephone</template>

                                                                    <input-telephone
                                                                        v-model="forms.persons[index].telephone"
                                                                        :sid="persons_sid"
                                                                        :name="`persons[${index}][telephone]`"
                                                                        :validator-name="`data.${index}.telephone`"
                                                                        :disabled="is_saving"
                                                                    />
                                                                </form-item>
                                                            </grid-layout-item>
                                                        </grid-layout>
                                                    </div>

                                                    <div class="px-4 pt-3 border-b">
                                                        <h6 class="mb-3 text-base font-normal">Address</h6>

                                                        <form-items-address
                                                            v-model="forms.persons[index].address"
                                                            :sid="persons_sid"
                                                            :name="`persons[${index}][address]`"
                                                            :validator-name="`data.${index}.address`"
                                                            :disabled="is_saving"
                                                        />
                                                    </div>

                                                    <div class="px-4 pt-3 border-b">
                                                        <grid-layout>
                                                            <grid-layout-item>
                                                                <h6 class="mb-3 text-base font-normal">Proof of Identity</h6>

                                                                <form-item-identity-document
                                                                    v-model="forms.persons[index].identity_card"
                                                                    :identity-document="person ? person.identity_card : null"
                                                                    :sid="persons_sid"
                                                                    :name="`persons[${index}][identity_card]`"
                                                                    :validator-name="`data.${index}.identity_card`"
                                                                    :disabled="is_saving || (person && person.status === person.consts.status.verified)"
                                                                    :is-required="true"
                                                                />
                                                            </grid-layout-item>
                                                        </grid-layout>
                                                    </div>

                                                    <div class="px-4 pt-3 border-b">
                                                        <grid-layout>
                                                            <grid-layout-item>
                                                                <h6 class="mb-3 text-base font-normal">Proof of Address</h6>

                                                                <form-item-identity-document
                                                                    v-model="forms.persons[index].proof_of_address"
                                                                    :identity-document="person ? person.proof_of_address : null"
                                                                    :sid="persons_sid"
                                                                    :name="`persons[${index}][proof_of_address]`"
                                                                    :validator-name="`data.${index}.proof_of_address`"
                                                                    :disabled="is_saving || (person && person.status === person.consts.status.verified)"
                                                                    :is-required="true"
                                                                />
                                                            </grid-layout-item>
                                                        </grid-layout>
                                                    </div>
                                                </template>

                                                <div class="px-4 pt-3">
                                                    <h6 class="mb-3 text-base font-normal">Position</h6>

                                                    <grid-layout>
                                                        <grid-layout-item>
                                                            <form-item
                                                                :sid="persons_sid"
                                                                :is-required="true"
                                                                :name="`persons[${index}][job_title]`"
                                                                :validator-name="`data.${index}.job_title`"
                                                            >
                                                                <template slot="label">Job Title</template>

                                                                <input-text
                                                                    v-model="forms.persons[index].job_title"
                                                                    :sid="persons_sid"
                                                                    :name="`persons[${index}][job_title]`"
                                                                    :validator-name="`data.${index}.job_title`"
                                                                    :disabled="is_saving"
                                                                />
                                                            </form-item>
                                                        </grid-layout-item>

                                                        <grid-layout-item>
                                                            <form-item
                                                                :sid="persons_sid"
                                                                :is-required="true"
                                                                :name="`persons[${index}][_roles]`"
                                                                :error-text="person && person.is_account_opener && $validator.has('meta.account_opener_roles', persons_sid) ? $validator.first('meta.account_opener_roles', persons_sid) : ''"
                                                            >
                                                                <template slot="label">Roles</template>

                                                                <grid-layout>
                                                                    <grid-layout-item class="mb-3 md:w-1/2">
                                                                        <label class="flex items-center cursor-pointer">
                                                                            <input-checkbox
                                                                                v-model="forms.persons[index].is_director"
                                                                                :sid="persons_sid"
                                                                                :name="`persons[${index}][is_director]`"
                                                                            />

                                                                            <div class="ml-3 leading-none mt-2px">Director</div>
                                                                        </label>
                                                                    </grid-layout-item>

                                                                    <grid-layout-item class="mb-3 md:w-1/2">
                                                                        <label class="flex items-center cursor-pointer">
                                                                            <input-checkbox
                                                                                v-model="forms.persons[index].is_executive"
                                                                                :sid="persons_sid"
                                                                                :name="`persons[${index}][is_executive]`"
                                                                            />

                                                                            <div class="ml-3 leading-none mt-2px">Execute</div>
                                                                        </label>
                                                                    </grid-layout-item>

                                                                    <grid-layout-item class="md:w-1/2">
                                                                        <label class="flex items-center cursor-pointer">
                                                                            <input-checkbox
                                                                                v-model="forms.persons[index].is_owner"
                                                                                :sid="persons_sid"
                                                                                :name="`persons[${index}][is_owner]`"
                                                                            />

                                                                            <div class="ml-3 leading-none mt-2px">Owner/Shareholder</div>
                                                                        </label>
                                                                    </grid-layout-item>
                                                                </grid-layout>
                                                            </form-item>
                                                        </grid-layout-item>

                                                        <grid-layout-item v-if="forms.persons[index].is_owner">
                                                            <form-item
                                                                :sid="persons_sid"
                                                                :is-required="true"
                                                                :name="`persons[${index}][percent_ownership]`"
                                                                :validator-name="`data.${index}.percent_ownership`"
                                                            >
                                                                <template slot="label">Percent Ownership</template>

                                                                <input-text
                                                                    v-model="forms.persons[index].percent_ownership"
                                                                    :sid="persons_sid"
                                                                    :disabled="is_saving"
                                                                    :name="`persons[${index}][percent_ownership]`"
                                                                    :validator-name="`data.${index}.percent_ownership`"
                                                                />
                                                            </form-item>
                                                        </grid-layout-item>
                                                    </grid-layout>
                                                </div>
                                            </div>
                                        </with-variables>
                                    </div>

                                    <button
                                        v-if="forms.persons.length <= 4"
                                        type="button"
                                        class="btn btn-blue-lightest"
                                        @click.prevent="addPerson"
                                    >
                                        <template v-if="forms.persons.length == 0">
                                            Add an person
                                        </template>
                                        <template v-else>
                                            Add a {{ forms.persons.length + 1 | ordinal }} person
                                        </template>
                                    </button>
                                </form-section>
                            </div>

                            <div class="px-4 py-3 text-right bg-grey-bg sm:px-6">
                                <base-button
                                    :is-loading="is_saving"
                                    class="w-full btn-blue md:w-auto"
                                >
                                    Update Account &amp; Continue
                                </base-button>
                            </div>

                            <div
                                v-for="(update_error, index) in errors"
                                :key="index"
                            >
                                <error-modal
                                    v-if="update_error && $error.is(update_error, { exclude: [400, 422] })"
                                    :error="update_error"
                                />
                            </div>
                        </base-form>
                    </promise-all>
                </persons-updates>
            </businesses-store>
        </div>
    </landlords-find>
</template>

<script>
    import _ from 'lodash'
    import scroll from '../../../../../support/scroll'
    import moment from 'moment'
    import LandlordsFind from '../../../../../components/landlords-find'
    import BusinessesStore from '../../../../../components/businesses-store'
    import FormItemIdentityDocument from '../../../../../components/form-item-identity-document'
    import FormItemsAddress from '../../../../../components/form-items-address'
    import PersonsUpdates from '../../../../../components/persons-updates'
    import PromiseAll from '../../../../../components/promise-all'

    export default {
        components: {
            BusinessesStore,
            FormItemIdentityDocument,
            FormItemsAddress,
            LandlordsFind,
            PersonsUpdates,
            PromiseAll
        },
        data () {
            return {
                forms: {
                    business: {
                        name: '',
                        telephone: null,
                        legal_name: '',
                        tax_id: '',
                        vat_id: '',
                        address: {
                            street: '',
                            locality: '',
                            village: '',
                            town: '',
                            county: '',
                            postcode: ''
                        },
                        proof_of_entity: null
                    },
                    persons: []
                }
            }
        },
        methods: {
            onLandlordsFind ({ data: landlord }) {
                if (landlord.account.business) {
                    this.forms.business.name = landlord.account.business.name
                    this.forms.business.legal_name = landlord.account.business.legal_name
                    this.forms.business.tax_id = landlord.account.business.tax_id
                    this.forms.business.vat_id = landlord.account.business.vat_id
                    this.forms.business.telephone = landlord.account.business.telephone
                        ? _.pick(landlord.account.business.telephone, ['suffix', 'prefix'])
                        : null
                    this.forms.business.address.street = landlord.account.business.address.street
                    this.forms.business.address.locality = landlord.account.business.address.locality
                    this.forms.business.address.village = landlord.account.business.address.village
                    this.forms.business.address.town = landlord.account.business.address.town
                    this.forms.business.address.county = landlord.account.business.address.county
                    this.forms.business.address.postcode = landlord.account.business.address.postcode
                }

                _.each(landlord.account.persons.data, person => {
                    this.forms.persons.push({
                        uuid: person.uuid,
                        first_name: person.first_name,
                        last_name: person.last_name,
                        date_of_birth: moment(person.date_of_birth).format('DD/MM/YYYY'),
                        email: person.email,
                        telephone: person.telephone
                            ? _.pick(person.telephone, ['suffix', 'prefix'])
                            : null,
                        job_title: person.job_title,
                        is_account_opener: person.is_account_opener,
                        is_director: person.is_director,
                        is_executive: person.is_executive,
                        is_owner: person.is_owner,
                        percent_ownership: person.percent_ownership,
                        address: {
                            street: person.address.street,
                            locality: person.address.locality,
                            village: person.address.village,
                            town: person.address.town,
                            county: person.address.county,
                            postcode: person.address.postcode
                        },
                        identity_card: null,
                        proof_of_address: null
                    })
                })
            },
            onSuccess ({ data: business }) {
                this.$track(
                    'dashboard.landlords.verification.busines.edit',
                    'form-submission',
                    'A landlord has completed step 2 (business) of verifying their account'
                )

                setTimeout(() => {
                    this.$router.push({
                        name: this.$router.fix('dashboard.*.verification.bank.edit')
                    })
                }, 0)
            },
            onError () {
                scroll.toTop()
            },
            findPerson (persons, uuid) {
                return _.chain(persons)
                    .get('data', [])
                    .find({ uuid })
                    .value()
            },
            addPerson () {
                this.forms.persons.push({
                    first_name: '',
                    last_name: '',
                    date_of_birth: '',
                    email: '',
                    telephone: null,
                    job_title: '',
                    is_director: false,
                    is_executive: false,
                    is_owner: false,
                    percent_ownership: '',
                    address: {
                        street: '',
                        locality: '',
                        village: '',
                        town: '',
                        county: '',
                        postcode: ''
                    },
                    identity_card: null,
                    proof_of_address: null
                })
            },
            removePerson (person, index) {
                this.forms.persons = _.remove(this.forms.persons, (person, j) => {
                    return index !== j
                })

                this.$validator.reset()
            }
        }
    }
</script>
